import { useMemo } from "react";
import type { Ref } from "react";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { ReportSettingsTarget } from "@common/helpers/report-settings/report-setting-candidates";
import { useReportTemplateSettings } from "@api/reportTemplate.api";
import { ReportSettingsEditorHandle } from "../ReportSettingsEditorDrawer";
import { PREVIEW_SAMPLE_ENTITY_NAME } from "../preview/reportPreviewData";
import { LayoutEditorLoadState } from "./LayoutEditorLoadState";
import { LayoutEditorShell } from "./LayoutEditorShell";
import { useTemplateReportSettingsDrafts } from "./useTemplateReportSettingsDrafts";

interface Props {
    templateId: string;
    isOpen: boolean;
    onRequestClose: () => void;
}

const NO_TEMPLATE_NAMES = {};

export const TemplateLayoutEditor = ({
    templateId,
    isOpen,
    onRequestClose,
    ref,
}: Props & { ref?: Ref<ReportSettingsEditorHandle> }) => {
    const { data, isPending, isFetching, refetch } =
        useReportTemplateSettings(templateId);
    const drafts = useTemplateReportSettingsDrafts({ templateId });
    const firm = useActiveOrganization();
    const target = useMemo<ReportSettingsTarget>(
        () => ({ kind: "template", templateId }),
        [templateId],
    );

    if (!data) {
        return (
            <LayoutEditorLoadState
                isPending={isPending || isFetching}
                onRetry={() => void refetch()}
            />
        );
    }

    return (
        <LayoutEditorShell
            ref={ref}
            drafts={drafts}
            target={target}
            targetKey={templateId}
            scope={{}}
            templateNames={NO_TEMPLATE_NAMES}
            canEdit
            entityPicker={null}
            entityName={PREVIEW_SAMPLE_ENTITY_NAME}
            firmName={firm?.name ?? null}
            firmLogoUrl={firm?.logoUrl ?? null}
            entityLogoUrl={null}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
        />
    );
};
